export type LocalizedText = { [locale: string]: string };

export type ArtworkKind = "ART" | "PHOTO";
export type ArtworkLicence = "PUBLIC_DOMAIN" | "CONTRIBUTOR";

/** Mirrors the Artwork entity. The generated client types jsonb as `object`. */
export interface Artwork {
  id: string;
  /** When the work was added; drives the "new" sort. */
  createdAt?: string;
  /** Bumped whenever the row changes, including a re-ingested master. */
  updatedAt?: string;
  kind: ArtworkKind;
  slug: string;
  title: LocalizedText;
  artist: string;
  year: number | null;
  description?: LocalizedText | null;
  sourceKey: string;
  widthPx: number;
  heightPx: number;
  licence: ArtworkLicence;
  rightsHolder?: string | null;
  attribution?: string | null;
  sourceUrl?: string | null;
  royaltyBps?: number | null;
  tags?: string[];
  featured?: boolean;
  active?: boolean;
  sortOrder?: number;
  collections?: ArtworkCollection[];
}

export interface ArtworkCollection {
  id: string;
  slug: string;
  title: LocalizedText;
  description?: LocalizedText | null;
  coverArtworkId?: string | null;
  sortOrder?: number;
  active?: boolean;
}

/**
 * The shop's own language, and the one everything else falls back to.
 *
 * Order matters, and it is not the obvious one. Falling back to Slovenian
 * first shows Slovenian titles to Polish and Italian customers, which is how
 * this went wrong before: the catalogue is authored in Slovenian, so `sl` is
 * populated for nearly everything and would win on every market. English is
 * the right default for a market whose own translation is missing — it is at
 * least a language the customer may read, and for a work of art the English
 * title is the one the rest of the world uses.
 */
export const localized = (
  text: LocalizedText | undefined | null,
  locale: string,
): string =>
  text?.[locale] ?? text?.en ?? text?.sl ?? Object.values(text ?? {})[0] ?? "";

/** Width divided by height. 1 when the master reports nothing usable. */
export const artworkAspect = (artwork: Pick<Artwork, "widthPx" | "heightPx">) =>
  artwork.heightPx > 0 ? artwork.widthPx / artwork.heightPx : 1;
